'use client'

import React from 'react'
import { DialogContent, DialogTitle } from '@/ui/components/Dialog'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { CMSButton } from '@/ui/components/CMSButton'
import { Label } from '@/ui/atoms/Label'
import { Profile } from '@/ui/atoms/Profile'
import { useTranslations } from 'next-intl'
import { Media, ButtonField, Vendor } from '@/payload-types'
import { EventHeader } from './EventHeader'
import RichText from '@/ui/components/RichText'
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import Link from 'next/link'
import { TypedLocale } from 'payload'

type AuthorData = {
  image?: Media | string | null
  name?: string
}

export type EventModalProps = {
  title: string
  startDate?: string | null
  endDate?: string | null
  location?: string | null
  category?: {
    title: string
    colorScheme: string
  }
  labels?: string[]
  image?: Media | string | null
  description?: SerializedEditorState | null
  organizer?: string | null
  organizerIsLinkable?: boolean | null
  organizerLinkType?: 'internal' | 'external' | null
  organizerVendor?: Vendor | string | null
  organizerLink?: string | null
  language?: string | null
  link?: ButtonField | null
  showCTAButton?: boolean | null
  secondLink?: ButtonField | null
  showSecondCTAButton?: boolean | null
  isRecommended?: boolean
  recommendedBy?: AuthorData[]
  recommendedWhy?: string | null
  longRecommendation?: string | null
  locale?: TypedLocale
}

export const EventModal: React.FC<EventModalProps> = ({
  title,
  startDate,
  endDate,
  location,
  category,
  labels,
  image,
  description,
  organizer,
  organizerIsLinkable,
  organizerLinkType,
  organizerVendor,
  organizerLink,
  language,
  link,
  showCTAButton,
  secondLink,
  showSecondCTAButton,
  isRecommended,
  recommendedBy,
  recommendedWhy,
  longRecommendation,
  locale,
}) => {
  const t = useTranslations('public')

  // Determine the organizer link URL
  const getOrganizerUrl = () => {
    if (!organizerIsLinkable) return null

    if (organizerLinkType === 'internal' && organizerVendor) {
      const vendor = typeof organizerVendor === 'object' ? organizerVendor : null
      return vendor?.path || null
    }

    if (organizerLinkType === 'external' && organizerLink) {
      return organizerLink
    }

    return null
  }

  const organizerUrl = getOrganizerUrl()
  const isInternalLink = organizerLinkType === 'internal'

  return (
    <DialogContent className="w-full max-w-3xl lg:max-w-5xl !p-0 flex flex-col !overflow-hidden">
      <DialogTitle className="sr-only">{title}</DialogTitle>

      {/* Header Image */}
      {image && (
        <div className="relative w-full aspect-[3/1] overflow-hidden flex-shrink-0">
          <CMSImage
            resource={typeof image === 'object' ? image : undefined}
            className="w-full h-full object-cover"
            fill
          />
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 lg:gap-8 p-6 overflow-y-auto">
        {/* Left Column */}
        <div className="space-y-6">
          <EventHeader
            title={title}
            startDate={startDate}
            endDate={endDate}
            location={location}
            organizer={organizer}
            showEventDate={true}
            titleAs="h2"
          />

          {/* Description */}
          {description && <RichText data={description} />}

          {/* Event Type */}
          {category && (
            <div className="flex flex-wrap gap-2">
              <Label title={category.title} colorScheme={category.colorScheme} size="small" />
            </div>
          )}

          {/* Topics/Tags */}
          {labels && labels.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-body-S-medium text-text-subtle">{t('topics')}</h3>
              <div className="flex flex-wrap gap-2">
                {labels.map((label) => <Label title={label} variant="neutral" key={label} />)}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-4 p-4 rounded-md bg-white">
          {/* Organizer */}
          {organizer && (
            <div>
              <h3 className="text-label-S-regular text-text-subtle">{t('organizer')}</h3>
              {organizerUrl ? (
                isInternalLink ? (
                  <Link
                    href={organizerUrl}
                    className="inline-flex items-center gap-1.5 text-body-M-regular text-text-brand hover:underline"
                  >
                    {organizer}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="fill-current"
                    >
                      <path d="M16.0039 9.414L7.39691 18.021L5.98291 16.607L14.5889 8H7.00391V6H18.0039V17H16.0039V9.414Z" />
                    </svg>
                  </Link>
                ) : (
                  <a
                    href={organizerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-body-M-regular text-text-brand hover:underline"
                  >
                    {organizer}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="fill-current"
                    >
                      <path d="M16.0039 9.414L7.39691 18.021L5.98291 16.607L14.5889 8H7.00391V6H18.0039V17H16.0039V9.414Z" />
                    </svg>
                  </a>
                )
              ) : (
                <p className="text-body-M-regular text-text-base">{organizer}</p>
              )}
            </div>
          )}

          {/* Location */}
          {location && (
            <div>
              <h3 className="text-label-S-regular text-text-subtle">{t('location')}</h3>
              <p className="text-body-M-regular text-text-base">{location}</p>
            </div>
          )}

          {/* Language */}
          {language && (
            <div>
              <h3 className="text-label-S-regular text-text-subtle">{t('language')}</h3>
              <p className="text-body-M-regular text-text-base">{language}</p>
            </div>
          )}

          {/* Recommended Event Box */}
          {isRecommended && recommendedBy && (
            <div className="bg-bg-brand-blue rounded-md p-4 space-y-4">
              <h3 className="text-label-XS-semibold text-text-subtle">{t('recommended-event')}</h3>
              {(longRecommendation || recommendedWhy) && (
                <p className="text-body-S-regular text-text-subtle">{`"${longRecommendation || recommendedWhy}"`}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {recommendedBy.map((author) => (
                  <Profile
                    key={author.name}
                    name={author.name}
                    image={typeof author.image === 'object' ? author.image : undefined}
                    size="medium"
                  />
                ))}
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          {showCTAButton && link && <CMSButton {...link} size="lg" className="w-full" locale={locale} />}
          {showSecondCTAButton && secondLink && (
            <CMSButton {...secondLink} appearance="secondary" size="lg" className="w-full" locale={locale} />
          )}
        </div>
      </div>
    </DialogContent>
  )
}
